/**
 * Shared design token scales used across layout primitives (Box, Flex, etc.) and the viewport
 * breakpoints. Hand-maintained alongside the four `tokens.*.css` files in `src/design-system/foundations/styles/` (ADR 0044,
 * ADR 0051) — this module adds the helpers and re-exports a few constants under their established
 * names; keep it in step by hand when a token in one of the lists below changes.
 */

/** Every custom-property name declared across the four `tokens.*.css` files. */
export type TokenName = `--${string}`;

/**
 * Resolved (light-theme) literal values for the tokens the Storybook foundation pages display
 * beside their name (`Motion`, `Radius`, `Shadows`) — kept in step by hand with `tokens.static.css` /
 * `tokens.light.css`.
 */
export const TOKEN_VALUES: Partial<Record<TokenName, string>> = {
    '--duration-instant': '50ms',
    '--duration-fast': '100ms',
    '--duration-normal': '200ms',
    '--duration-slow': '300ms',
    '--duration-slower': '500ms',
    '--duration-slowest': '700ms',
    '--ease-linear': 'linear',
    '--ease-in': 'cubic-bezier(0.4, 0, 1, 1)',
    '--ease-out': 'cubic-bezier(0, 0, 0.2, 1)',
    '--ease-in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
    '--ease-spring': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    '--ease-decelerate': 'cubic-bezier(0, 0, 0.2, 1)',
    '--ease-accelerate': 'cubic-bezier(0.4, 0, 1, 1)',
    '--radius-none': '0px',
    '--radius-xs': '2px',
    '--radius-sm': '4px',
    '--radius-md': '8px',
    '--radius-lg': '12px',
    '--radius-full': '9999px',
    '--radius-input': '4px',
    '--radius-tooltip': '4px',
    '--radius-button': '8px',
    '--radius-card': '8px',
    '--radius-panel': '12px',
    '--radius-modal': '12px',
    '--radius-chip': '9999px',
    '--shadow-nav': '0 2px 10px 0 var(--shadow-color-nav)',
    '--shadow-button': '0 2px 10px 0 var(--shadow-color-button)',
    '--shadow-surface': '0 1px 2px 0 var(--shadow-color-elevation-key)',
    '--shadow-elevated':
        '0 4px 8px 3px var(--shadow-color-elevation-ambient), 0 3px 3px 0 var(--shadow-color-elevation-key)',
    '--shadow-dropdown': '0 3px 6px 0 var(--shadow-color-drop), 0 2px 2px 0 var(--shadow-color-drop)',
    '--shadow-scroll': '0 6px 6px -6px var(--shadow-color-nav)',
    '--shadow-focus':
        '0 0 0 var(--focus-ring-offset) var(--focus-ring-gap), 0 0 0 calc(var(--focus-ring-offset) + var(--focus-ring-width)) var(--focus-ring)',
    '--shadow-focus-elevated': 'var(--shadow-focus), var(--shadow-elevated)',
    '--shadow-focus-inset':
        'inset 0 0 0 var(--focus-ring-offset) var(--focus-ring-gap), inset 0 0 0 calc(var(--focus-ring-offset) + var(--focus-ring-width)) var(--focus-ring)',
};

/** The `--space-*` steps. Mirrors the scale declared in `src/design-system/foundations/styles/tokens.static.css`. */
export type SpaceScale = 0 | 1 | 2 | 3 | 4 | 6 | 8 | 12 | 16 | 20;

/**
 * Resolves a `SpaceScale` key to its `--space-*` token reference.
 * Returns `undefined` for an unset value so the caller can omit the property entirely.
 *
 * @example
 * space(4)        // 'var(--space-4)'
 * space(undefined) // undefined
 */
export const space = (value?: SpaceScale): string | undefined => (value == null ? undefined : `var(--space-${value})`);

/**
 * A vertical (stack) gap: a `SpaceScale` step, or `'section'` for the page-section rhythm —
 * `--stack-section`, 48px on desktop and 40px below `BP_DESKTOP`, which is not a step of the scale.
 */
export type StackGap = SpaceScale | 'section';

/**
 * Resolves a `StackGap` to its token reference: a step to `--space-*`, `'section'` to `--stack-section`.
 *
 * @example
 * stackGap(4)         // 'var(--space-4)'
 * stackGap('section') // 'var(--stack-section)'
 */
export const stackGap = (value?: StackGap): string | undefined =>
    value === 'section' ? 'var(--stack-section)' : space(value);

/**
 * Viewport breakpoints — the single source of truth for every `@media` px in CSS and every width
 * test in JS (ADR 0042, ADR 0045). CSS custom properties cannot be read inside `@media` or
 * `matchMedia`, so CSS modules write the literal px with a comment naming the constant, and
 * `src/guards/tokenConsumers.test.ts` fails on any literal that is not one of these values (or its
 * `- 1` complement in a `max-width`).
 *
 * The values and their rationale:
 *
 *   Mobile    0 – 599      below BP_TABLET
 *   Tablet    600 – 1199   BP_TABLET up to BP_DESKTOP − 1
 *   Desktop   1200 +       BP_DESKTOP and above
 *
 * These are the three modes of Figma's Breakpoint variable collection (`layout/breakpoint-min`).
 * BP_DESKTOP is where the nav collapses and the tablet/mobile type and space scale applies
 * (ADR 0054). Above it the layout does not change — the page is a centred 1128px column and only
 * the margins grow — so there is no wide band. One implementation-only value remains:
 * BP_TABLE_CARD (wide tables become cards). It is not a design breakpoint — do not add new
 * consumers of it without asking design.
 */
export const BP_TABLET = 600;
export const BP_TABLE_CARD = 768;
export const BP_DESKTOP = 1200;
export const BREAKPOINTS = [BP_TABLET, BP_TABLE_CARD, BP_DESKTOP] as const;

/**
 * `matchMedia` query for the mobile band (below `BP_TABLET`) — drives the Sheet presentation mode
 * of overlay panels (FilterMenu / SortMenu) and Modal's full-viewport takeover.
 */
export const MOBILE_MEDIA_QUERY = `(max-width: ${BP_TABLET - 1}px)`;

/**
 * Below this width a wide data table (more than three columns) renders one card per row instead
 * of a `<Table>`. The react-responsive call sites use `{ maxWidth: BP_TABLE_CARD - 1 }` for the
 * same switch, so every list flips layout at the same point.
 */
export const TABLE_CARD_MEDIA_QUERY = `(max-width: ${BP_TABLE_CARD - 1}px)`;
